import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useContext, useState } from "react";
import mainLogo from "../../assets/images/main-favicon.png";
import { apiGet } from "../../api/apiGet";
import { LoginContext } from "../../contexts/LoginContext";
import { AlertMessageContext } from "../atoms/AlertMessage/AlertMessage";
import { Link } from "react-router-dom";
import { resetErrors } from "../../store/slices/LoadingSlice/LoadingSlice";
import { useDispatch, useSelector } from "react-redux";

export default function Header() {
  const { setAuthenticated } = useContext(LoginContext);
  const alertMessage = useContext(AlertMessageContext);
  const { logoImage } = useSelector((state) => state.admin);

  const dispatch = useDispatch();

  const logOut = async () => {
    try {
      await apiGet.logOut();
      setAuthenticated(false);
    } catch (error) {
      alertMessage.handleOpen(
        "error-logging-out",
        "danger",
        `Error logging out. ${error}`,
        10000
      );
    }
  };

  const [navButtons, setNavButtons] = useState([
    { title: "Home", href: "/", visible: true, selected: "/" },
    {
      title: "History",
      href: "/view-history",
      visible: true,
      selected: "/view-history",
    },
  ]);

  const [profileButtons] = useState([
    {
      title: "Your Profile",
      href: "#",
      visible: false,
      onClick: undefined,
    },
    {
      title: "Settings",
      href: "/settings",
      visible: true,
      onClick: undefined,
    },
    { title: "Sign Out", href: "#", visible: true, onClick: () => logOut() },
  ]);

  const doNavigation = (title) => {
    dispatch(resetErrors());

    setNavButtons(
      navButtons.map((btn) => ({
        ...btn,
        selected: btn.title === title,
      }))
    );
  };

  return (
    <Disclosure as="nav" className="bg-white/40 shadow sticky z-50">
      <div className="mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex h-16 justify-between">
          <div className="flex px-2 lg:px-0">
            <div className="flex shrink-0 items-center">
              <img
                alt={process.env.REACT_APP_COMPANY}
                src={logoImage?.image_url || mainLogo}
                className="h-8 w-auto"
              />
            </div>
            <div className="hidden lg:ml-6 lg:flex lg:space-x-8">
              {navButtons
                .filter((btn) => btn.visible)
                .map((btn) => {
                  return (
                    <Link
                      key={btn.title}
                      to={btn.href}
                      onClick={() => doNavigation(btn.title)}
                      className={
                        "inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium " +
                        (window.location.href.endsWith(btn.selected)
                          ? "border-[var(--primary-color)] text-gray-900"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700")
                      }
                    >
                      {btn.title}
                    </Link>
                  );
                })}
            </div>
          </div>
          <div className="flex flex-1 items-center justify-center px-2 lg:ml-6 lg:justify-end">
            {/* <div className="grid w-full max-w-lg grid-cols-1 lg:max-w-xs">
              <input
                name="search"
                type="search"
                placeholder="Search"
                className="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-[var(--primary-color)] sm:text-sm/6"
              />
              <MagnifyingGlassIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400"
              />
            </div> */}
          </div>
          <div className="flex items-center lg:hidden">
            {/* Mobile menu button */}
            <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[var(--primary-color)]">
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Open main menu</span>
              <Bars3Icon
                aria-hidden="true"
                className="block size-6 group-data-[open]:hidden"
              />
              <XMarkIcon
                aria-hidden="true"
                className="hidden size-6 group-data-[open]:block"
              />
            </DisclosureButton>
          </div>
          <div className="hidden lg:ml-4 lg:flex lg:items-center">
            {/* <button
              type="button"
              className="relative shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-2"
            >
              <span className="absolute -inset-1.5" />
              <span className="sr-only">View notifications</span>
              <BellIcon aria-hidden="true" className="size-6" />
            </button> */}

            {/* Profile dropdown */}
            <Menu as="div" className="relative ml-4 shrink-0">
              <div>
                <MenuButton className="relative flex rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:ring-offset-2">
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">Open user menu</span>
                  <img
                    alt="Default profile"
                    src="	https://www.gravatar.com/avatar/?d=mp"
                    className="size-8 rounded-full"
                  />
                </MenuButton>
              </div>
              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
              >
                {profileButtons
                  .filter((btn) => btn.visible)
                  .map((btn, idx) => {
                    return (
                      <MenuItem key={idx}>
                        <Link
                          key={btn.title}
                          to={btn.href}
                          className="block px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:outline-none"
                          onClick={btn.onClick}
                        >
                          {btn.title}
                        </Link>
                      </MenuItem>
                    );
                  })}
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>
      <DisclosurePanel className="lg:hidden text-right">
        <div className="space-y-1 px-2 pb-3 pt-2">
          {navButtons
            .filter((btn) => btn.visible)
            .map((btn) => (
              <DisclosureButton
                key={btn.title}
                as={Link}
                to={btn.href}
                onClick={() => doNavigation(btn.title)}
                className={
                  (window.location.href.endsWith(btn.selected)
                    ? "bg-gray-100 text-[var(--primary-color)]"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900") +
                  " block rounded-md px-3 py-2 text-base font-medium"
                }
              >
                {btn.title}
              </DisclosureButton>
            ))}
        </div>

        <div className="border-t border-gray-200 pt-4 pb-3">
          <div className="mt-3 space-y-1 px-2">
            {profileButtons
              .filter((btn) => btn.visible)
              .map((btn, idx) => (
                <DisclosureButton
                  key={idx}
                  as={Link}
                  to={btn.href}
                  onClick={btn.onClick}
                  className="block rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                >
                  {btn.title}
                </DisclosureButton>
              ))}
          </div>
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
}
